import {
  Link,
  List,
  Alert,
  alpha,
  Stack,
  ListItem,
  useTheme,
  Typography,
  Grid2 as Grid,
} from '@mui/material';

import { useChannelContext } from 'src/pages/channel/hooks';
import { AuthLoader } from 'src/pages/auth/components/shared/AuthLoader';
import { PasswordField, ChannelStepLayout } from 'src/pages/channel/shared';
import { MultiSelectAutocomplete } from 'src/pages/tickets/components/custom-multiSelect';

import { Form, Field } from 'src/components/hook-form';

import { useLineStep3 } from '../hooks/useLineStep3';

export default function LineStep3() {
  const theme = useTheme();
  const { isLoading } = useChannelContext();

  const {
    t,
    methods,
    onSubmit,
    isNextDisabled,
    _recordList,
    searchValue,
    isFetching,
    openList,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  } = useLineStep3();

  const steps = [
    <>
      <Link
        href="https://developers.line.biz/console"
        target="_blank"
        sx={{
          fontWeight: 600,
          color: 'primary.main',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
            color: 'primary.dark',
          },
        }}
      >
        {t('LINE_DEVELOPERS_CONSOLE')}
      </Link>{' '}
      {t('LINE_STEP3_RETRIEVE_CHANNEL_DETAILS')}
    </>,
  ];

  return (
    <>
      {isLoading && <AuthLoader />}
      <Form methods={methods} onSubmit={onSubmit}>
        <Stack spacing={4} sx={{ height: '100%' }}>
          <ChannelStepLayout
            title={t('CHANNEL_CONFIGURATION')}
            subtitle={t('CONFIGURE_LINE_CHANNEL_SETTINGS')}
            isNextDisabled={isNextDisabled}
          >
            {/* Setup Instructions */}
            <Alert
              severity="info"
              sx={{
                mb: 4,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.08),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                '& .MuiAlert-icon': {
                  color: 'info.main',
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('SETUP_INSTRUCTIONS')}
              </Typography>
              <List sx={{ py: 0 }}>
                {steps.map((step, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      p: 0,
                      mb: 0.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.5,
                      }}
                    >
                      {step}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Alert>

            {/* Form Section */}

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field.Text
                  name="channelName"
                  label={t('CHANNEL_NAME')}
                  type="text"
                  placeholder={t('ENTER_CHANNEL_NAME')}
                  InputLabelProps={{ shrink: true }}
                  slotProps={{
                    input: {
                      sx: {
                        borderRadius: 2,
                        '&:focus-within': {
                          borderColor: 'primary.main',
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <MultiSelectAutocomplete
                  name="assignUsers"
                  label={t('ASSIGNED_TO')}
                  placeholder={`+ ${t('RECIPIENTS')}`}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  options={_recordList}
                  searchValue={searchValue}
                  listScrollHandler={listScrollHandler}
                  searchValueHandler={searchValueHandler}
                  open={openList}
                  openListHandler={openListHandler}
                  closeListHandler={closeListHandler}
                  isFetching={isFetching}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Field.Text
                  name="lineChannelId"
                  label={t('LINE_CHANNELID')}
                  placeholder={t('ENTER_LINE_CHANNELID')}
                  type="text"
                  InputLabelProps={{ shrink: true }}
                  slotProps={{
                    input: {
                      sx: {
                        borderRadius: 2,
                        '&:focus-within': {
                          borderColor: 'primary.main',
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <PasswordField
                  name="lineChannelSecret"
                  label={t('LINE_CHANNEL_SECRET')}
                  placeholder={t('ENTER_LINE_CHANNEL_SECRET')}
                />
              </Grid>
            </Grid>
          </ChannelStepLayout>
        </Stack>
      </Form>
    </>
  );
}
